/**
 * Label Formatting Utilities
 * Functions for formatting and processing text labels and strings
 */

/**
 * Takes a string phrase and breaks it into separate phrases
 * no bigger than 'maxwidth', breaks are made at complete words.
 * 
 * Note: Current implementation only returns the first word.
 * This may need to be enhanced to properly handle line breaking.
 *
 * @param {string} str - Label text to format
 * @param {number} maxwidth - Width in characters (currently unused)
 * @returns {string} Formatted label (currently just the first word)
 */
export const formatLabel = (str, maxwidth) => {
  return str.split(" ")[0];
};

/**
 * Advanced label formatting with proper word wrapping
 * Breaks text into lines that don't exceed maxwidth
 * 
 * @param {string} str - Text to format
 * @param {number} maxwidth - Maximum width in characters
 * @returns {Array<string>} Array of lines that fit within maxwidth
 */
export const formatLabelMultiLine = (str, maxwidth) => {
  const words = str.split(" ");
  const lines = [];
  let currentLine = "";

  words.forEach(word => {
    if ((currentLine + word).length <= maxwidth) {
      currentLine += (currentLine ? " " : "") + word;
    } else {
      if (currentLine) {
        lines.push(currentLine);
      }
      currentLine = word;
    }
  });

  if (currentLine) {
    lines.push(currentLine);
  }

  return lines;
};

/**
 * Truncate text with ellipsis if it exceeds maxLength
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length before truncation
 * @returns {string} Truncated text with ellipsis if needed
 */
export const truncateText = (text, maxLength) => {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 3) + "...";
};

/**
 * Convert text to title case
 * @param {string} str - Text to convert
 * @returns {string} Text in title case
 */
export const toTitleCase = (str) => {
  return str.replace(/\w\S*/g, (txt) => 
    txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );
};

/**
 * Format a count with a unit label (``1 Day``, ``15 Workers``, etc.).
 *
 * @param {number} count - Numeric count.
 * @param {string} singular - Singular unit label (e.g. ``Day``, ``Worker``).
 * @param {string} [plural] - Plural unit label; defaults to ``${singular}s``.
 * @returns {string}
 */
export function formatCountLabel(count, singular, plural = `${singular}s`) {
  const n = Number(count) || 0;
  return `${n} ${n === 1 ? singular : plural}`;
}
